//
// Media picker for a single image or video from the media library, with upload of new files.
// Markup: <div data-media-picker data-accept="image|video" data-media="{json}"><input type="hidden"></div>.
// The data-media JSON comes from \CustomerHub\Media::formatForPicker().
//
import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import XHR from '@uppy/xhr-upload';

const ICONS = {
	image: 'fa-regular fa-image',
	video: 'fa-regular fa-video'
};

let modal = null;
let modalElement = null;
let activePicker = null;
let uppy = null;
let currentPage = 1;
let searchTimer = null;

//
// Creates an element with optional classes and text.
//
const createElement = (tag, className, text) => {

	const element = document.createElement(tag);
	if (className) {
		element.className = className;
	}
	if (text) {
		element.textContent = text;
	}
	return element;
};

//
// Creates a thumbnail, or an icon when the media item has no thumbnail.
//
const createThumbnail = (media, accept) => {

	if (media.thumbnail_url) {
		const image = createElement('img', 'media-picker-thumbnail');
		image.src = media.thumbnail_url;
		image.alt = media.original_file_name;
		return image;
	}

	const placeholder = createElement('div', 'media-picker-thumbnail media-picker-thumbnail--icon');
	placeholder.appendChild(createElement('i', ICONS[accept] + ' fa-2x text-muted'));
	return placeholder;
};

//
// Renders the selected media item, or an empty state, inside a picker.
//
const renderPicker = (picker) => {

	const accept = picker.dataset.accept;
	const media = picker.mediaData;
	const input = picker.querySelector('input[type="hidden"]');

	let view = picker.querySelector('.media-picker-view');
	if (view) {
		view.remove();
	}

	view = createElement('div', 'media-picker-view d-flex align-items-center gap-3');

	if (media) {
		view.appendChild(createThumbnail(media, accept));
		view.appendChild(createElement('div', 'media-picker-file-name', media.original_file_name));
	} else {
		view.appendChild(createElement('div', 'text-muted', 'None selected'));
	}

	const buttons = createElement('div', 'ms-auto d-flex gap-2');

	const chooseButton = createElement('button', 'btn btn-sm btn-outline-primary', 'Choose');
	chooseButton.type = 'button';
	chooseButton.addEventListener('click', () => openModal(picker));
	buttons.appendChild(chooseButton);

	if (media) {
		const removeButton = createElement('button', 'btn btn-sm btn-outline-secondary', 'Remove');
		removeButton.type = 'button';
		removeButton.addEventListener('click', () => {
			picker.mediaData = null;
			input.value = '';
			renderPicker(picker);
		});
		buttons.appendChild(removeButton);
	}

	view.appendChild(buttons);
	picker.appendChild(view);
};

//
// Sets the chosen media item on the active picker and closes the modal.
//
const selectMedia = (media) => {

	if (!activePicker) {
		return;
	}

	activePicker.mediaData = media;
	activePicker.querySelector('input[type="hidden"]').value = media.mediaid;
	renderPicker(activePicker);
	modal.hide();
};

//
// Loads a page of media items from the library into the modal grid.
//
const loadLibrary = async (page) => {

	const grid = modalElement.querySelector('[data-media-picker-grid]');
	const moreButton = modalElement.querySelector('[data-media-picker-more]');
	const accept = activePicker.dataset.accept;

	const body = new FormData();
	body.append('format', accept);
	body.append('search', modalElement.querySelector('[data-media-picker-search]').value);
	body.append('page', page);

	const response = await fetch('/include/ajax/media_picker.php', { method: 'POST', body });
	const data = await response.json();

	if (page === 1) {
		grid.innerHTML = '';
	}

	if (page === 1 && data.rows.length === 0) {
		grid.appendChild(createElement('div', 'col-12 text-muted py-4 text-center', 'No media found.'));
	}

	data.rows.forEach((media) => {

		const column = createElement('div', 'col');
		const card = createElement('button', 'media-picker-card w-100 text-start');
		card.type = 'button';
		card.appendChild(createThumbnail(media, accept));
		card.appendChild(createElement('div', 'media-picker-file-name small mt-1', media.original_file_name));
		card.addEventListener('click', () => selectMedia(media));
		column.appendChild(card);
		grid.appendChild(column);
	});

	currentPage = data.page;
	moreButton.classList.toggle('d-none', data.page >= data.total_pages);
};

//
// Creates the shared modal and the upload widget the first time a picker is opened.
//
const createModal = () => {

	modalElement = createElement('div', 'modal fade');
	modalElement.tabIndex = -1;
	modalElement.innerHTML = `
		<div class="modal-dialog modal-xl modal-dialog-scrollable">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title" data-media-picker-title></h5>
					<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
				</div>
				<div class="modal-body">
					<ul class="nav nav-tabs" role="tablist">
						<li class="nav-item" role="presentation">
							<button class="nav-link active" data-bs-toggle="tab" data-bs-target="#media-picker-library" type="button" role="tab">Select from library</button>
						</li>
						<li class="nav-item" role="presentation">
							<button class="nav-link" data-bs-toggle="tab" data-bs-target="#media-picker-upload" type="button" role="tab">Upload new</button>
						</li>
					</ul>
					<div class="tab-content pt-3">
						<div class="tab-pane fade show active" id="media-picker-library" role="tabpanel">
							<input type="text" class="form-control mb-3" placeholder="Search media..." data-media-picker-search>
							<div class="row row-cols-2 row-cols-md-4 g-3" data-media-picker-grid></div>
							<div class="text-center mt-3">
								<button type="button" class="btn btn-outline-secondary d-none" data-media-picker-more>Load more</button>
							</div>
						</div>
						<div class="tab-pane fade" id="media-picker-upload" role="tabpanel">
							<div data-media-picker-uppy></div>
						</div>
					</div>
				</div>
			</div>
		</div>`;
	document.body.appendChild(modalElement);

	modal = new window.bootstrap.Modal(modalElement);

	modalElement.querySelector('[data-media-picker-search]').addEventListener('input', () => {
		clearTimeout(searchTimer);
		searchTimer = setTimeout(() => loadLibrary(1), 300);
	});

	modalElement.querySelector('[data-media-picker-more]').addEventListener('click', () => {
		loadLibrary(currentPage + 1);
	});

	uppy = new Uppy({
		id: 'media-picker',
		autoProceed: true,
		restrictions: { maxNumberOfFiles: 1 }
	})
		.use(Dashboard, {
			target: modalElement.querySelector('[data-media-picker-uppy]'),
			inline: true,
			height: 350,
			width: '100%',
			proudlyDisplayPoweredByUppy: false
		})
		.use(XHR, {
			endpoint: '/include/ajax/upload.php'
		});

	uppy.on('upload-success', (file, response) => {

		const data = response.body;
		uppy.clear();
		if (!data || !data.mediaid) {
			return;
		}

		// A new upload is still on local disk, so there is no thumbnail to show yet.
		selectMedia({
			mediaid: data.mediaid,
			original_file_name: data.original_file_name,
			format: data.format,
			thumbnail_url: null
		});
	});
};

//
// Opens the modal for a picker, limited to the picker's media type.
//
const openModal = (picker) => {

	if (!modal) {
		createModal();
	}

	activePicker = picker;
	const accept = picker.dataset.accept;

	modalElement.querySelector('[data-media-picker-title]').textContent = 'Choose ' + accept;
	modalElement.querySelector('[data-media-picker-search]').value = '';
	uppy.setOptions({ restrictions: { maxNumberOfFiles: 1, allowedFileTypes: [accept + '/*'] } });

	loadLibrary(1);
	modal.show();
};

window.addEventListener('DOMContentLoaded', () => {

	document.querySelectorAll('[data-media-picker]').forEach((picker) => {

		picker.mediaData = JSON.parse(picker.dataset.media || 'null');
		renderPicker(picker);
	});
});
